import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take, catchError, of, filter, withLatestFrom } from 'rxjs';
import {
  LucideAngularModule,
  AlertTriangle,
  X,
  Clock,
  ArrowRight,
  FlaskConical,
} from 'lucide-angular';
import { AuthService } from '../../../common/auth/auth.service';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';
import { StudentAssignmentService } from './services/student-assignment.service';
import {
  AssignmentPreview,
  StudentAssignmentStep,
  StudentAssignmentErrorType,
  SubmissionStatus,
  STUDENT_ASSIGNMENT_THEME_COLOR,
  SaveAnswerDto,
} from './services/student-assignment.models';
import { AssignmentPreviewComponent } from './components/preview/assignment-preview';
import { QuestionViewComponent } from './components/question/question-view';
import { QuizTimerComponent } from './components/timer/quiz-timer';
import { QuizResultComponent } from './components/result/quiz-result';

/**
 * Student Assignment Wizard
 * Students access this via /a/:shareCode
 *
 * Flow:
 * 1. Preview - Shows assignment info, "Start" button
 * 2. Auth (if not logged in) - Redirects to auth page with studentShareCode
 * 3. Questions - One question per page with timer
 * 4. Result - Shows grade after submission
 */
@Component({
  selector: 'app-student-assignment',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    AssignmentPreviewComponent,
    QuestionViewComponent,
    QuizTimerComponent,
    QuizResultComponent,
  ],
  templateUrl: './student-assignment.html',
  styleUrl: './student-assignment.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentAssignmentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly assignmentService = inject(StudentAssignmentService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly themeColor = STUDENT_ASSIGNMENT_THEME_COLOR;
  protected readonly StudentAssignmentStep = StudentAssignmentStep;
  protected readonly StudentAssignmentErrorType = StudentAssignmentErrorType;

  protected readonly icons = {
    AlertTriangle,
    X,
    Clock,
    ArrowRight,
    FlaskConical,
  };

  // Leave confirmation modal
  protected readonly isLeaveModalOpen = signal(false);

  // Test mode: teacher testing their own assignment
  protected readonly isTestMode = signal(false);
  // Retest confirmation modal (shown when teacher already has a completed test submission)
  protected readonly isRetestModalOpen = signal(false);

  // Observables from service
  protected readonly currentStep$ = this.assignmentService.currentStep$;
  protected readonly preview$ = this.assignmentService.preview$;
  protected readonly submission$ = this.assignmentService.submission$;
  protected readonly currentQuestion$ = this.assignmentService.currentQuestion$;
  protected readonly currentQuestionIndex$ = this.assignmentService.currentQuestionIndex$;
  protected readonly localAnswers$ = this.assignmentService.localAnswers$;
  protected readonly isLoading$ = this.assignmentService.isLoading$;
  protected readonly isSubmitting$ = this.assignmentService.isSubmitting$;
  protected readonly isGrading$ = this.assignmentService.isGrading$;
  protected readonly gradingProgressStep$ = this.assignmentService.gradingProgressStep$;
  protected readonly result$ = this.assignmentService.result$;
  protected readonly error$ = this.assignmentService.error$;
  protected readonly errorType$ = this.assignmentService.errorType$;
  protected readonly expiresAt$ = this.assignmentService.expiresAt$;

  // For template
  protected shareCode: string | null = null;
  protected isTeacherError = false;

  // Stored for analytics properties
  private assignmentPreview: AssignmentPreview | null = null;

  ngOnInit(): void {
    const shareCode = this.route.snapshot.paramMap.get('shareCode');
    const startQuiz = this.route.snapshot.queryParams['startQuiz'] === 'true';

    if (!shareCode) {
      this.router.navigate(['/']);
      return;
    }

    this.shareCode = shareCode;

    // Clear the query param if present
    if (startQuiz) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }

    this.setupResultTracking();
    this.setupErrorTracking();

    // Load preview first (public, no auth needed)
    this.assignmentService
      .loadPreview(shareCode)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null))
      )
      .subscribe((preview) => {
        if (!preview) return;

        this.assignmentPreview = preview;
        this.amplitudeService.trackEvent(
          'student_assignment_preview_viewed',
          {
            share_code: this.shareCode,
            assignment_id: preview.id,
            subject: preview.subject,
            question_count: preview.questionCount,
            has_time_limit: !!preview.timeLimit,
            has_due_date: !!preview.dueDate,
            is_test_mode: this.isTestMode(),
          },
        );

        // Check if user is authenticated
        this.authService
          .isAuthenticated()
          .pipe(take(1))
          .subscribe((isAuth) => {
            if (isAuth) {
              this.handleAuthenticatedUser(startQuiz);
            } else {
              // Not authenticated - stay on preview
              this.assignmentService.setStep(StudentAssignmentStep.PREVIEW);
            }
          });
      });
  }

  private handleAuthenticatedUser(startQuiz: boolean): void {
    // Fetch profile (uses cache if available) to reliably check role
    this.authService.getProfile().pipe(take(1)).subscribe((user) => {
      if (this.authService.hasTeacherRole(user)) {
        // Check if this teacher owns the assignment (test mode)
        const preview = this.assignmentPreview;
        if (preview?.teacherId && preview.teacherId === user.id) {
          // Owner teacher — enter test mode
          this.isTestMode.set(true);
          this.handleSubmissionFlow(startQuiz);
        } else {
          // Non-owner teacher — block access
          this.isTeacherError = true;
          this.assignmentService.setStep(StudentAssignmentStep.ERROR);
        }
        return;
      }

      // Student - proceed normally
      this.handleSubmissionFlow(startQuiz);
    });
  }

  /**
   * Shared submission flow for both students and teacher test mode.
   * Checks for existing submission and acts accordingly.
   */
  private handleSubmissionFlow(startQuiz: boolean): void {
    this.assignmentService
      .loadSubmission()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response.started || !response.submission) {
          // No submission exists yet
          if (startQuiz) {
            // Coming from auth redirect - start the quiz
            this.startSubmissionWithTracking();
          } else {
            // Show preview
            this.assignmentService.setStep(StudentAssignmentStep.PREVIEW);
          }
          return;
        }

        // Handle based on submission status
        const { submission } = response;
        if (
          submission.status === SubmissionStatus.GRADED ||
          submission.status === SubmissionStatus.EXPIRED
        ) {
          if (this.isTestMode()) {
            // Teacher has already completed a test — show retest modal
            this.isRetestModalOpen.set(true);
            this.assignmentService.setStep(StudentAssignmentStep.PREVIEW);
          } else {
            // Student — show result
            this.assignmentService.loadResult().subscribe();
          }
        } else {
          // In progress - resume questions
          this.trackStartedEvent(true);
          this.assignmentService.setStep(StudentAssignmentStep.QUESTIONS);
          this.trackQuestionViewedEvent();
        }
      });
  }

  // ============================================
  // PREVIEW ACTIONS
  // ============================================

  protected onStartAssignment(): void {
    this.amplitudeService.trackEvent('student_assignment_start_clicked', {
      share_code: this.shareCode,
      assignment_id: this.assignmentPreview?.id,
      is_test_mode: this.isTestMode(),
    });

    // Check if user is authenticated
    this.authService
      .isAuthenticated()
      .pipe(take(1))
      .subscribe((isAuth) => {
        if (isAuth) {
          // Fetch profile (uses cache if available) to reliably check role
          this.authService.getProfile().pipe(take(1)).subscribe((user) => {
            if (this.authService.hasTeacherRole(user)) {
              const preview = this.assignmentPreview;
              if (preview?.teacherId && preview.teacherId === user.id) {
                // Owner teacher — test mode, start the assignment
                this.isTestMode.set(true);
                this.startSubmissionWithTracking();
              } else {
                // Non-owner teacher — block
                this.isTeacherError = true;
                this.assignmentService.setStep(StudentAssignmentStep.ERROR);
              }
              return;
            }

            // Start the assignment (student)
            this.startSubmissionWithTracking();
          });
        } else {
          this.amplitudeService.trackEvent(
            'student_assignment_auth_redirect',
            {
              share_code: this.shareCode,
              is_test_mode: this.isTestMode(),
            },
          );

          // Redirect to auth with full redirect URL
          const redirectUrl = `/a/${this.shareCode}?startQuiz=true`;
          this.router.navigate(['/sign-up'], {
            queryParams: { redirectUrl, userType: 'student' },
          });
        }
      });
  }

  // ============================================
  // QUESTION ACTIONS
  // ============================================

  protected onAnswerChange(answer: SaveAnswerDto): void {
    const question = this.assignmentService.questions[
      this.assignmentService.currentQuestionIndex
    ];
    if (question) {
      this.assignmentService.saveAnswer(question.id, answer);
    }
  }

  protected onNextQuestion(): void {
    this.assignmentService.nextQuestion();
    this.trackQuestionViewedEvent();
    this.scrollToTop();
  }

  protected onPreviousQuestion(): void {
    this.assignmentService.prevQuestion();
    this.trackQuestionViewedEvent();
    this.scrollToTop();
  }

  protected onSubmit(): void {
    this.trackSubmittedEvent(false);

    this.assignmentService
      .submitAndGrade()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected onTimeExpired(): void {
    this.trackSubmittedEvent(true);

    this.assignmentService.handleTimeExpired();
  }

  // ============================================
  // LEAVE / DASHBOARD ACTIONS
  // ============================================

  protected onLeaveClick(): void {
    this.isLeaveModalOpen.set(true);
  }

  protected onStay(): void {
    this.isLeaveModalOpen.set(false);
  }

  protected onLeaveToDashboard(): void {
    this.isLeaveModalOpen.set(false);
    if (this.isTestMode()) {
      this.router.navigate(['/assignment', this.assignmentPreview?.id, 'students']);
    } else {
      this.router.navigate(['/student/dashboard/assignments']);
    }
  }

  // ============================================
  // RESULT ACTIONS
  // ============================================

  protected onContinue(): void {
    this.amplitudeService.trackEvent('student_assignment_completed', {
      share_code: this.shareCode,
      assignment_id: this.assignmentPreview?.id,
      is_test_mode: this.isTestMode(),
    });

    if (this.isTestMode()) {
      // Navigate back to teacher's assignment view
      this.router.navigate(['/assignment', this.assignmentPreview?.id, 'students']);
    } else {
      // Navigate to student dashboard
      this.router.navigate(['/student/dashboard/assignments']);
    }
  }

  // ============================================
  // RETEST ACTIONS (Teacher test mode)
  // ============================================

  /**
   * Called from the result page when the teacher clicks "Test Again".
   * Opens the retest confirmation modal.
   */
  protected onRetestFromResult(): void {
    this.isRetestModalOpen.set(true);
  }

  /**
   * Confirms the retest: resets submission state (soft-delete will happen on the
   * backend when the teacher clicks "Start Assignment" on the preview step)
   * and navigates back to PREVIEW.
   */
  protected onRetestConfirm(): void {
    this.isRetestModalOpen.set(false);
    this.assignmentService.resetForRetest();
    this.assignmentService.setStep(StudentAssignmentStep.PREVIEW);
  }

  protected onRetestCancel(): void {
    this.isRetestModalOpen.set(false);
    // Navigate back to teacher's assignment view
    this.router.navigate(['/assignment', this.assignmentPreview?.id, 'students']);
  }

  // ============================================
  // HELPERS
  // ============================================

  protected getLocalAnswer(
    answers: Map<string, SaveAnswerDto>,
    questionId: string
  ): SaveAnswerDto | null {
    return answers.get(questionId) ?? null;
  }

  protected getTotalQuestions(): number {
    return this.assignmentService.totalQuestions;
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ============================================
  // ANALYTICS HELPERS
  // ============================================

  /**
   * Start submission and track started + initial question viewed events.
   */
  private startSubmissionWithTracking(): void {
    this.assignmentService
      .startSubmission()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.trackStartedEvent(false);
        this.trackQuestionViewedEvent();
      });
  }

  private trackStartedEvent(isResumed: boolean): void {
    this.amplitudeService.trackEvent('student_assignment_started', {
      share_code: this.shareCode,
      assignment_id: this.assignmentPreview?.id,
      question_count: this.assignmentService.totalQuestions,
      is_resumed: isResumed,
      is_test_mode: this.isTestMode(),
    });
  }

  private trackQuestionViewedEvent(): void {
    const index = this.assignmentService.currentQuestionIndex;
    const question = this.assignmentService.questions[index];

    this.amplitudeService.trackEvent('student_assignment_question_viewed', {
      share_code: this.shareCode,
      question_index: index,
      question_type: question?.type ?? null,
      total_questions: this.assignmentService.totalQuestions,
      is_test_mode: this.isTestMode(),
    });
  }

  private trackSubmittedEvent(wasAutoSubmitted: boolean): void {
    const questions = this.assignmentService.questions;
    const questionsAnswered = questions.filter((q) => {
      const answer = this.assignmentService.getLocalAnswer(q.id);
      return (
        answer &&
        (answer.selectedOptionId || answer.textAnswer?.trim())
      );
    }).length;

    this.amplitudeService.trackEvent('student_assignment_submitted', {
      share_code: this.shareCode,
      assignment_id: this.assignmentPreview?.id,
      questions_answered: questionsAnswered,
      total_questions: this.assignmentService.totalQuestions,
      was_auto_submitted: wasAutoSubmitted,
      is_test_mode: this.isTestMode(),
    });
  }

  /**
   * Reactively track result_viewed when the result becomes available.
   * Handles manual submit, auto-submit (timer), and loading existing results.
   */
  private setupResultTracking(): void {
    this.assignmentService.result$
      .pipe(
        filter((result): result is NonNullable<typeof result> => result !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        const durationSeconds = this.calculateDurationSeconds(
          result.startedAt,
          result.submittedAt,
        );

        this.amplitudeService.trackEvent(
          'student_assignment_result_viewed',
          {
            share_code: this.shareCode,
            assignment_id: this.assignmentPreview?.id,
            score_percentage: result.gradePercentage,
            performance_level: this.getPerformanceLevel(
              result.gradePercentage,
            ),
            duration_seconds: durationSeconds,
            is_test_mode: this.isTestMode(),
          },
        );
      });
  }

  /**
   * Reactively track error_displayed when the ERROR step is shown.
   */
  private setupErrorTracking(): void {
    this.currentStep$
      .pipe(
        filter((step) => step === StudentAssignmentStep.ERROR),
        withLatestFrom(this.errorType$),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([_, errorType]) => {
        this.amplitudeService.trackEvent(
          'student_assignment_error_displayed',
          {
            share_code: this.shareCode,
            error_type: this.isTeacherError
              ? 'teacher_access'
              : errorType || 'unknown',
            is_test_mode: this.isTestMode(),
          },
        );
      });
  }

  private getPerformanceLevel(
    percentage: number,
  ): 'excellent' | 'good' | 'needs_improvement' {
    if (percentage >= 80) return 'excellent';
    if (percentage >= 60) return 'good';
    return 'needs_improvement';
  }

  private calculateDurationSeconds(
    startedAt: string,
    submittedAt: string,
  ): number | null {
    if (!startedAt || !submittedAt) return null;
    const ms =
      new Date(submittedAt).getTime() - new Date(startedAt).getTime();
    return ms > 0 ? Math.floor(ms / 1000) : null;
  }
}
